import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APIResponse } from '../models/APIResponse';
import {
  DepthMeasurementType,
  FinalReportDetailsModel,
  FinalSampleInputsModel,
  SamplingInformation,
  SamplingOpeningRow,
} from '../models/sample';
import { NUMBERS_CONSTANTS } from '../common/numbers-constants';
import { saveAction, updateAction, zeroString } from '../common/texts-constants';
import { SampleCreationService } from './sample-creation.service';

type SaveMode = typeof saveAction | typeof updateAction;
type ApiUnknown = APIResponse<unknown>;

interface FinalReportDetailsBindingState {
  isEditMode: boolean;
  finalReportDetailsId: number;

  frontPagePatchValue: {
    productionInterval: string;
    reportDate: string | null;
  };

  coverLetterPatchValue: {
    reportToPrefix: string;
    reportTo: string;
    fromAddress1: string;
    fromAddress2: string;
    fromAddress3: string;
    fromAddress4: string;
    toAddress1: string;
    toAddress2: string;
    toAddress3: string;
    toAddress4: string;
    regards: string;
    by: string;
    attachment: string;
    productionIntervalCover: string;
    comments: string;
  };

  crewList: string[];
}


@Injectable({
  providedIn: 'root',
})
export class FinalSampleInputsService {
  private sampleService = inject(SampleCreationService);

  GetDepthMeasurementTypes(): Observable<DepthMeasurementType[]> {
    return this.sampleService.GetDepthMeasurementTypeDropdowns().pipe(
      map((response) => (response?.isSuccess && response?.result ? response.result : []))
    );
  }

  GetFinalSampleInputs(labTestId: number): Observable<FinalSampleInputsModel | undefined> {
    return this.sampleService.GetFinalSampleInputs(labTestId).pipe(
      map((response) => {
        if (!response?.isSuccess || !response?.result) {
          return undefined;
        }

        const result = response.result as FinalSampleInputsModel | FinalSampleInputsModel[];
        return Array.isArray(result) ? result[NUMBERS_CONSTANTS.zero] : result;
      })
    );
  }

  GetSamplingInformationRows(labTestId: number): Observable<SamplingOpeningRow[]> {
    return this.sampleService.GetSamplingInformation(labTestId).pipe(
      map((response) => (response?.isSuccess && response?.result ? response.result.rows || [] : []))
    );
  }

  GetFinalReportDetails(labTestId: number): Observable<FinalReportDetailsModel | undefined> {
    return this.sampleService.GetFinalReportDetails(labTestId).pipe(
      map((response) => {
        if (!response?.isSuccess || !response?.result) {
          return undefined;
        }
        const result = response.result as FinalReportDetailsModel | FinalReportDetailsModel[];
        return Array.isArray(result) ? result[NUMBERS_CONSTANTS.zero] : result;
      })
    );
  }


  SaveSamplingInformation(samplingInformation: SamplingInformation): Observable<ApiUnknown> {
    return this.sampleService.SaveSamplingInformation(samplingInformation);
  }

  SaveFinalSampleInputs(
    action: SaveMode,
    formValue: unknown,
    finalSampleInputId: number,
    labTestId: number,
    userId: string
  ): Observable<ApiUnknown> {
    const payload = this.BuildFinalSampleInputsModel(action, formValue, finalSampleInputId, labTestId, userId);
    const dateToUse: Date = payload.dateSampled ? new Date(payload.dateSampled) : new Date();
    dateToUse.setHours(NUMBERS_CONSTANTS.twelve, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero);
    const wirePayload: FinalSampleInputsModel = { ...payload, dateSampled: new Date(this.ToDateOnly(dateToUse)) };

    return action === saveAction
      ? this.sampleService.SaveFinalSampleInputs(wirePayload, action)
      : this.sampleService.UpdateFinalSampleInputs(wirePayload, action);
  }

  SaveFinalReportDetails(
    action: SaveMode,
    frontPageFormValue: unknown,
    coverLetterFormValue: unknown,
    labCrew: string[],
    finalReportDetailsId: number,
    labTestId: number,
    userId: string,
    includeCoverLetterData: boolean,
    existingModel?: FinalReportDetailsModel
  ): Observable<ApiUnknown> {
    const payload = this.BuildFinalReportDetailsModel(
      action,
      frontPageFormValue,
      coverLetterFormValue,
      labCrew,
      finalReportDetailsId,
      labTestId,
      userId,
      includeCoverLetterData,
      existingModel
    );

    const wirePayload: FinalReportDetailsModel = {
      ...payload,
      reportDate: payload.reportDate ? new Date(this.ToDateOnly(payload.reportDate)) : null
    };

    return this.sampleService.SaveFinalReportDetails(wirePayload, action)
  }



  ToFinalSampleInputFormValue(model: FinalSampleInputsModel): Record<string, string | number | null> {
    return {
      flowingTopDepth: this.ToNumberOrNull(model.flowingTopDepth),
      flowingBottomDepth: this.ToNumberOrNull(model.flowingBottomDepth),
      upperPerforationDepth: this.ToNumberOrNull(model.upperPerforationDepth),
      lowerPerforationDepth: this.ToNumberOrNull(model.lowerPerforationDepth),
      volumeTransferred: this.ToNumberOrNull(model.volumeTransferred),
      sampleVolume: this.ToNumberOrNull(model.sampleVolume),
      reservoirAge: this.ToStringOrEmpty(model.reservoirAge),
      depthMeasurementType: this.ToNumberOrNull(model.depthMeasurementType),
      reservoirFluid: this.ToStringOrEmpty(model.reservoirFluid),
      samplingDepth: this.ToStringOrEmpty(model.samplingDepth),
      testType: this.ToStringOrEmpty(model.testType),
      samplingCompany: this.ToStringOrEmpty(model.samplingCompany),
      dateSampled: model.dateSampled ? this.ToLocalNoonNoZ(model.dateSampled) : null,
    };
  }

  ToFinalReportDetailsBindingState(model?: FinalReportDetailsModel): FinalReportDetailsBindingState {
    if (!model) {
      return {
        isEditMode: false,
        finalReportDetailsId: NUMBERS_CONSTANTS.zero,
        frontPagePatchValue: {
          productionInterval: '',
          reportDate: null
        },
        coverLetterPatchValue: {
          reportToPrefix: '',
          reportTo: '',
          fromAddress1: '',
          fromAddress2: '',
          fromAddress3: '',
          fromAddress4: '',
          toAddress1: '',
          toAddress2: '',
          toAddress3: '',
          toAddress4: '',
          regards: '',
          by: '',
          attachment: '',
          productionIntervalCover: '',
          comments: ''
        },
        crewList: ['']
      };
    }

    const hasValidId =
      !!model.finalReportDetailsId &&
      model.finalReportDetailsId > NUMBERS_CONSTANTS.zero;

    const hasAnyValue =
      (!!model.productionInterval && model.productionInterval.trim().length > NUMBERS_CONSTANTS.zero) ||
      !!model.reportDate ||
      (!!model.labCrew && model.labCrew.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.reportToPrefix && model.reportToPrefix.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.reportTo && model.reportTo.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.fromAddress1 && model.fromAddress1.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.fromAddress2 && model.fromAddress2.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.fromAddress3 && model.fromAddress3.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.fromAddress4 && model.fromAddress4.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.toAddress1 && model.toAddress1.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.toAddress2 && model.toAddress2.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.toAddress3 && model.toAddress3.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.toAddress4 && model.toAddress4.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.regards && model.regards.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.by && model.by.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.attachment && model.attachment.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.productionIntervalCover && model.productionIntervalCover.trim().length > NUMBERS_CONSTANTS.zero) ||
      (!!model.comments && model.comments.trim().length > NUMBERS_CONSTANTS.zero);

    const crewRaw = (model.labCrew ?? '').trim();
    const crewList =
      crewRaw.length > NUMBERS_CONSTANTS.zero
        ? crewRaw
          .split(',')
          .map((value) => value.trim())
          .filter((value) => value.length > NUMBERS_CONSTANTS.zero)
        : [''];

    return {
      isEditMode: hasValidId || hasAnyValue,
      finalReportDetailsId: hasValidId ? model.finalReportDetailsId : NUMBERS_CONSTANTS.zero,
      frontPagePatchValue: {
        productionInterval: model.productionInterval ?? '',
        reportDate: model.reportDate ? this.ToLocalNoonNoZ(model.reportDate as Date) : null
      },
      coverLetterPatchValue: {
        reportToPrefix: model.reportToPrefix ?? '',
        reportTo: model.reportTo ?? '',
        fromAddress1: model.fromAddress1 ?? '',
        fromAddress2: model.fromAddress2 ?? '',
        fromAddress3: model.fromAddress3 ?? '',
        fromAddress4: model.fromAddress4 ?? '',
        toAddress1: model.toAddress1 ?? '',
        toAddress2: model.toAddress2 ?? '',
        toAddress3: model.toAddress3 ?? '',
        toAddress4: model.toAddress4 ?? '',
        regards: model.regards ?? '',
        by: model.by ?? '',
        attachment: model.attachment ?? '',
        productionIntervalCover: model.productionIntervalCover ?? '',
        comments: model.comments ?? ''
      },
      crewList
    };
  }


  CanSelectRecombine(rows: SamplingOpeningRow[]): boolean {
    const selectedCount = rows.filter((row) => row.isRecombine === true).length;
    return selectedCount < NUMBERS_CONSTANTS.two;
  }

  private BuildFinalSampleInputsModel(
    action: SaveMode,
    formValue: unknown,
    finalSampleInputId: number,
    labTestId: number,
    userId: string
  ): FinalSampleInputsModel {
    const value = (formValue as Record<string, unknown>) ?? {};

    return {
      finalSampleInputId: action === updateAction ? finalSampleInputId ?? NUMBERS_CONSTANTS.zero : NUMBERS_CONSTANTS.zero,
      flowingTopDepth: this.ToStringOrEmpty(value['flowingTopDepth']),
      flowingBottomDepth: this.ToStringOrEmpty(value['flowingBottomDepth']),
      depthMeasurementType: this.ToStringOrEmpty(value['depthMeasurementType']),
      upperPerforationDepth: this.ToStringOrEmpty(value['upperPerforationDepth']),
      lowerPerforationDepth: this.ToStringOrEmpty(value['lowerPerforationDepth']),
      volumeTransferred: this.ToStringOrEmpty(value['volumeTransferred']),
      sampleVolume: this.ToStringOrEmpty(value['sampleVolume']),
      reservoirFluid: this.ToStringOrEmpty(value['reservoirFluid']),
      reservoirAge: this.ToStringOrEmpty(value['reservoirAge']),
      samplingDepth: this.ToStringOrEmpty(value['samplingDepth']),
      testType: this.ToStringOrEmpty(value['testType']),
      dateSampled: value['dateSampled'] ? this.ToLocalNoon(value['dateSampled'] as Date) : undefined,
      samplingCompany: this.ToStringOrEmpty(value['samplingCompany']),
      labTestId: Number(labTestId),
      createdUserId: userId,
      updatedUserId: action === updateAction ? userId : undefined,
    };
  }

  private BuildFinalReportDetailsModel(
    action: SaveMode,
    frontPageFormValue: unknown,
    coverLetterFormValue: unknown,
    labCrew: string[],
    finalReportDetailsId: number,
    labTestId: number,
    userId: string,
    includeCoverLetterData: boolean,
    existingModel?: FinalReportDetailsModel
  ): FinalReportDetailsModel {
    const frontPageValue = (frontPageFormValue as Record<string, unknown>) ?? {};
    const coverLetterValue = (coverLetterFormValue as Record<string, unknown>) ?? {};
    const existing = existingModel ?? ({} as FinalReportDetailsModel);

    const crewValues = labCrew
      .map((entry) => (entry ?? '').trim())
      .filter((entry) => entry.length > NUMBERS_CONSTANTS.zero);

    return {
      finalReportDetailsId:
        action === updateAction
          ? finalReportDetailsId ?? NUMBERS_CONSTANTS.zero
          : NUMBERS_CONSTANTS.zero,
      productionInterval: this.ToStringOrEmpty(frontPageValue['productionInterval']),
      reportDate: frontPageValue['reportDate']
        ? this.ToLocalNoon(frontPageValue['reportDate'] as Date)
        : null,
      labCrew: crewValues.join(', '),
      reportToPrefix: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['reportToPrefix'])
        : this.ToStringOrEmpty(existing.reportToPrefix),
      reportTo: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['reportTo'])
        : this.ToStringOrEmpty(existing.reportTo),
      fromAddress1: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['fromAddress1'])
        : this.ToStringOrEmpty(existing.fromAddress1),
      fromAddress2: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['fromAddress2'])
        : this.ToStringOrEmpty(existing.fromAddress2),
      fromAddress3: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['fromAddress3'])
        : this.ToStringOrEmpty(existing.fromAddress3),
      fromAddress4: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['fromAddress4'])
        : this.ToStringOrEmpty(existing.fromAddress4),
      toAddress1: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['toAddress1'])
        : this.ToStringOrEmpty(existing.toAddress1),
      toAddress2: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['toAddress2'])
        : this.ToStringOrEmpty(existing.toAddress2),
      toAddress3: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['toAddress3'])
        : this.ToStringOrEmpty(existing.toAddress3),
      toAddress4: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['toAddress4'])
        : this.ToStringOrEmpty(existing.toAddress4),
      regards: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['regards'])
        : this.ToStringOrEmpty(existing.regards),
      by: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['by'])
        : this.ToStringOrEmpty(existing.by),
      attachment: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['attachment'])
        : this.ToStringOrEmpty(existing.attachment),
      productionIntervalCover: includeCoverLetterData ? this.ToStringOrEmpty(coverLetterValue['productionIntervalCover'])
        : this.ToStringOrEmpty(existing.productionIntervalCover),
      comments: includeCoverLetterData
        ? this.ToStringOrEmpty(coverLetterValue['comments'])
        : this.ToStringOrEmpty(existing.comments),
      labTestId: Number(labTestId),
      createdUserId: userId,
      updatedUserId: action === updateAction ? userId : undefined
    };
  }


  private ToDateOnly(dateValue: Date): string {
    const dt = new Date(dateValue);
    const year = dt.getFullYear();
    const month = String(dt.getMonth() + NUMBERS_CONSTANTS.one).padStart(NUMBERS_CONSTANTS.two, NUMBERS_CONSTANTS.zeroString);
    const day = String(dt.getDate()).padStart(NUMBERS_CONSTANTS.two, NUMBERS_CONSTANTS.zeroString);
    return `${year}-${month}-${day}`;
  }

  private ToLocalNoonNoZ(dateValue: Date): string {
    const dt = new Date(dateValue);
    const year = dt.getFullYear();
    const month = String(dt.getMonth() + NUMBERS_CONSTANTS.one).padStart(NUMBERS_CONSTANTS.two, NUMBERS_CONSTANTS.zeroString);
    const day = String(dt.getDate()).padStart(NUMBERS_CONSTANTS.two, NUMBERS_CONSTANTS.zeroString);
    return `${year}-${month}-${day}T12:00:00`;
  }

  private ToLocalNoon(value: Date): Date {
    const dateValue = new Date(value);
    dateValue.setHours(NUMBERS_CONSTANTS.twelve, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero);
    return dateValue;
  }

  private ToNumberOrNull(value: unknown): number | null {
    if (value === NUMBERS_CONSTANTS.zero || value === zeroString) {
      return NUMBERS_CONSTANTS.zero;
    }

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? null : numberValue;
  }

  private ToStringOrEmpty(value: unknown): string {
    return value === null || value === undefined ? '' : String(value);
  }

}
